import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, Loader2, Clock, RefreshCw } from 'lucide-react';

const SIGN_IN_TIMEOUT_MS = 30000;

const LoginForm = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timedOut, setTimedOut] = useState(false);
  const timeoutRef = useRef(null);

  const clearSignInTimeout = () => {
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef?.current);
      timeoutRef.current = null;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setError('');
    setTimedOut(false);
    setIsLoading(true);

    // Start 30-second timeout
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setTimedOut(true);
      setError('Sign-in is taking longer than expected. Please check your connection and try again.');
    }, SIGN_IN_TIMEOUT_MS);

    try {
      const result = await signIn(email, password);
      clearSignInTimeout();
      // Navigate immediately after successful sign-in — don't wait for auth state propagation
      if (result && result?.data && result?.data?.user) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      clearSignInTimeout();
      setIsLoading(false);
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      clearSignInTimeout();
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    setTimedOut(false);
    setIsLoading(false);
    handleSubmit({ preventDefault: () => {} });
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 text-muted-foreground" />
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="Enter your email address"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
              disabled={isLoading}
              autoComplete="email"
            />
          </div>
        </div>

        {/* Password Field */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-4 w-4 text-muted-foreground" />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              placeholder="Enter your password"
              className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
              disabled={isLoading}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Forgot Password */}
        <div className="flex justify-end">
          <a href="/password-reset" className="text-sm text-primary hover:text-primary/80 font-medium">
            Forgot your password?
          </a>
        </div>

        {/* Error / Timeout Message */}
        {error && (
          <div className={`p-3 rounded-lg border ${
            timedOut
              ? 'bg-amber-500/10 border-amber-500/30' :'bg-destructive/10 border-destructive/20'
          }`}>
            <div className="flex items-start gap-2">
              {timedOut && <Clock className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
              <p className={`text-sm ${
                timedOut ? 'text-amber-600 dark:text-amber-400' : 'text-destructive'
              }`}>{error}</p>
            </div>
            {timedOut && (
              <button
                type="button"
                onClick={handleRetry}
                className="mt-2 flex items-center gap-1.5 text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try Again
              </button>
            )}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 px-4 bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Signing In...
            </>
          ) : (
            'Sign In'
          )}
        </button>

        {/* Register Link */}
        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{' '}
          <a href="/register" className="text-primary hover:text-primary/80 font-medium">
            Sign up
          </a>
        </p>
      </form>
    </div>
  );
};

export default LoginForm;